import { useId } from "react";

import { DiagramDecisionGate } from "@/app/features/diagrams/components/shared/DiagramDecisionGate";
import { DiagramFrame } from "@/app/features/diagrams/components/shared/DiagramFrame";
import { DiagramMarkers } from "@/app/features/diagrams/components/shared/DiagramMarkers";
import { DiagramNode } from "@/app/features/diagrams/components/shared/DiagramNode";
import {
  type DiagramTopologySpec,
  validateDiagramTopology,
} from "@/app/features/diagrams/functions/diagramTypes";

// Dev-mode topology specification check for referential integrity
const pipelineTopologySpec: DiagramTopologySpec = {
  nodes: [
    "fragmented-sources",
    "contract-boundary",
    "contract-gate",
    "downstream-processing",
    "quarantine-dlq",
  ],
  junctions: [],
  connectors: [
    {
      id: "sources-to-boundary",
      source: { nodeId: "fragmented-sources", anchor: "bottom-center" },
      target: { nodeId: "contract-boundary", anchor: "top-center" },
      meaning: "Raw payloads enter ingestion boundary",
      semanticType: "control",
      lineStyle: "solid",
    },
    {
      id: "boundary-to-gate",
      source: { nodeId: "contract-boundary", anchor: "bottom-center" },
      target: { nodeId: "contract-gate", anchor: "top-center" },
      meaning: "Payload checked against schema enforcer",
      semanticType: "control",
      lineStyle: "solid",
    },
    {
      id: "gate-to-downstream",
      source: { nodeId: "contract-gate", anchor: "left-center" },
      target: { nodeId: "downstream-processing", anchor: "top-center" },
      meaning: "Valid contract payload flows downstream",
      semanticType: "success",
      lineStyle: "solid",
    },
    {
      id: "gate-to-quarantine",
      source: { nodeId: "contract-gate", anchor: "right-center" },
      target: { nodeId: "quarantine-dlq", anchor: "top-center" },
      meaning: "Schema drift / contract breach quarantined",
      semanticType: "failure",
      lineStyle: "dashed",
    },
  ],
};

if (process.env.NODE_ENV !== "production") {
  validateDiagramTopology(pipelineTopologySpec);
}

export function DataContractBoundaryQuarantineVisual() {
  const titleId = useId();
  const descId = useId();

  return (
    <DiagramFrame
      width={660}
      height={410}
      title="Data Contract Boundary & Quarantine Architecture"
      description="Architecture diagram showing fragmented multi-source data validated against a boundary contract schema, routing valid records to downstream processing while quarantining schema breaches into a dead-letter queue."
      titleId={titleId}
      descId={descId}
      viewBox="0 0 700 435"
      frame={{
        kind: "boundary",
        label: "CONTRACT VALIDATION BOUNDARY",
        x: 20,
        y: 20,
        width: 660,
        height: 395,
      }}
    >
      <DiagramMarkers prefix="cb-" />

      {/* STAGE 1: Fragmented Data Sources (Y: 52 to 96) */}
      <DiagramNode
        x={210}
        y={52}
        width={280}
        height={44}
        label="Fragmented Data Sources"
        sublabel="CRMs, External APIs & Telemetry"
        variant="capsule"
      />

      {/* Stem 1: Raw sources -> Boundary Validator */}
      <path
        d="M 350 96 L 350 134"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        opacity="0.45"
        markerEnd="url(#cb-arrow-neutral)"
      />

      {/* STAGE 2: Ingestion Boundary Validator (Y: 134 to 182) */}
      <DiagramNode
        x={190}
        y={134}
        width={320}
        height={48}
        label="Ingestion Boundary Validator"
        sublabel="Data Contract Schema & SLA Bounds"
        variant="brass"
      />

      {/* Stem 2: Boundary -> Decision Gate */}
      <path
        d="M 350 182 L 350 216"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        opacity="0.45"
      />

      {/* STAGE 3: Decision Gate (Diamond centered at X=350, Y=236) */}
      <DiagramDecisionGate x={312} y={216} label="VALID?" />

      {/* BRANCH 1: Valid Contract -> Left Exit -> Downstream Processing (Emerald Green) */}
      <path
        d="M 312 236 L 190 236 L 190 318"
        fill="none"
        stroke="#10b981"
        strokeWidth="1.8"
        markerEnd="url(#cb-arrow-green)"
      />
      <text
        x="251"
        y="227"
        textAnchor="middle"
        fill="#10b981"
        className="text-[11px] font-mono font-bold"
      >
        valid payload
      </text>

      {/* BRANCH 2: Schema Breach / Drift -> Right Exit -> Quarantine DLQ (Rose Red Dashed) */}
      <path
        d="M 388 236 L 510 236 L 510 318"
        fill="none"
        stroke="#e11d48"
        strokeWidth="1.6"
        strokeDasharray="5 4"
        markerEnd="url(#cb-arrow-fallback)"
      />
      <text
        x="449"
        y="227"
        textAnchor="middle"
        fill="#e11d48"
        className="text-[11px] font-mono font-bold"
      >
        schema breach / drift
      </text>

      {/* STAGE 4: Downstream Processing (Emerald Green Border, Y: 320 to 370) */}
      <DiagramNode
        x={70}
        y={320}
        width={240}
        height={50}
        label="Downstream Processing"
        sublabel="Normalized Pipeline & Analytics"
        variant="success"
      />

      {/* STAGE 5: Quarantine / Dead Letter Queue (Rose Red Border, Y: 320 to 370) */}
      <DiagramNode
        x={390}
        y={320}
        width={240}
        height={50}
        label="Quarantine / DLQ"
        sublabel="Breach Logs & Alert Trigger"
        variant="fallback"
      />
    </DiagramFrame>
  );
}
